/** What the upload returns, and what a turn then carries so the backend can find the file. */
export interface ClassifyAttachmentRef {
  attachment_id: string;
  filename: string;
  kind: 'pdf' | 'image';
}

// A current access token. KeycloakService is imported only when an upload happens, not at the top of the
// file: it pulls in keycloak-js, an ES-only package that Jest cannot parse, and nothing else here needs it.
const freshAccessToken = async (): Promise<string | undefined> => {
  const { default: KeycloakService } = await import('services/KeycloakService');
  try {
    await KeycloakService.updateToken();
  } catch {
    // Send what we have: the server answers an expired token with a "sign in again" message.
  }
  return KeycloakService.getToken();
};

/**
 * The Authorization header for a request that must prove who is asking: `{ Authorization: 'Bearer <token>' }`,
 * or `{}` when there is no token (the server then answers with its own "sign in again" message).
 *
 * A plain chat turn needs none. A turn that CARRIES FILES does: the server checks the caller is on the team
 * before it reads a file, so the session and message requests send this too, not just the upload.
 */
export const classifyAuthHeaders = async (): Promise<Record<string, string>> => {
  const token = await freshAccessToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
};

/**
 * Uploads one file for a Classify / Import conversation, to Classify's own endpoint
 * (POST /classify/hts/attachments). The endpoint checks who is asking: it needs the user's token and the team
 * the file is for, and it takes the uploader's identity from the token, so no user id is sent.
 *
 * A refusal comes back as an Error carrying the server's own reason (expired session, not on the team, type or
 * size), which is what the user and a bug report need.
 */
export const uploadClassifyAttachment = async (
  file: File,
  clfId: string,
  groupId: string,
  apiUrl: string,
): Promise<ClassifyAttachmentRef> => {
  const headers = await classifyAuthHeaders();
  const formData = new FormData();
  formData.append('clf_id', clfId);
  formData.append('group_id', groupId);
  formData.append('file', file);
  const response = await fetch(`${apiUrl}/classify/hts/attachments`, {
    method: 'POST',
    // No Content-Type: the browser sets it, with the multipart boundary.
    headers: headers,
    body: formData,
  });
  if (!response.ok) {
    const detail = await response.json().catch(() => null);
    throw new Error(detail?.detail || `Upload failed for ${file.name}`);
  }
  return response.json();
};
